import { useCallback } from 'react';

import { iamLaunchAbsorbSso } from '@crossnokaye/typescript-sdk/funcs/iamLaunchAbsorbSso.js';
import { LaunchAbsorbSsoResponse } from '@crossnokaye/typescript-sdk/models/operations';

import { ABSORB_LOGIN_FALLBACK_URL } from '@/components/navigation/left/constants';
import { useAtlasClient } from '@/hooks/use-atlas-client';

/**
 * Resolves the Absorb LMS (ATLAS Learning) launch URL.
 *
 * Calls the `/absorb/sso` endpoint for an authenticated SSO redirect and
 * returns the URI. On any failure (or an empty response) it returns the plain
 * Absorb login screen so the user always has a path into the LMS.
 *
 * Shared by both entry points: the left-nav item (opens a new tab) and the
 * `/learning` redirect route used by Pendo (same-tab `location.replace`).
 */
export const resolveAbsorbSsoUrl = async (
  client: ReturnType<typeof useAtlasClient>
): Promise<string> => {
  try {
    const result = await client<never, LaunchAbsorbSsoResponse>({
      atlasFunc: iamLaunchAbsorbSso,
    });

    return result.string ?? ABSORB_LOGIN_FALLBACK_URL;
  } catch {
    return ABSORB_LOGIN_FALLBACK_URL;
  }
};

/**
 * Returns a handler that opens ATLAS Learning (Absorb LMS) in a new tab.
 */
export const useAbsorbRedirect = () => {
  const client = useAtlasClient();

  return useCallback(async () => {
    window.open(await resolveAbsorbSsoUrl(client), '_blank');
  }, [client]);
};
